// client/src/components/pages/ErrorDetection/ErrorDetailsModal.tsx (converted from .js, WBE
// batch ST-00223)
/**
 * Error Details Modal
 * Shows full error detection results with summary stats,
 * severity thresholds, and a detailed results table.
 *
 * WBE-12: /results carries only a sample of each rule's error rows (a busy rule stores
 * 20,000+), so when the sample is not all of them this window loads the latest run's rows
 * from /results/:ruleId/details (at most 5,000; the CSV and Excel exports carry every row).
 */

import { useEffect, useState, type ComponentType } from 'react';
import {
  X,
  MousePointerClick,
  ChevronRight,
  CheckCircle,
  Clock,
  Hash,
  Download,
  FileSpreadsheet,
  FileText,
  Loader2,
} from 'lucide-react';
import { getSeverityConfig as getSeverityConfigJs } from './severityConfig';
import { SeverityBadge, SeverityIcon, totalErrorRows } from './ErrorCard';
import ErrorRecordModalJs from './ErrorRecordModal';
import api from '../../../services/api';
import type {
  ErrorDetailRow,
  ErrorDetailsResponse,
  ErrorDetectionResult,
  SeverityStyle,
} from '../../../types/clientPages/errorDetection';

// Typed views of the (still JavaScript) severity table and record window.
const getSeverityConfig = getSeverityConfigJs as unknown as (severity: string) => SeverityStyle;
const ErrorRecordModal = ErrorRecordModalJs as unknown as ComponentType<{
  errorRecord: ErrorDetailRow;
  errorResult: ErrorDetectionResult;
  onClose: () => void;
}>;

type ExportFormat = 'csv' | 'excel' | 'pdf';

/** The PDF export refuses more rows than this (routes/errorDetectionExport). */
const PDF_MAX_ROWS = 1000;

interface ErrorDetailsModalProps {
  errorResult: ErrorDetectionResult;
  onClose: () => void;
}

/** An axios error as far as this window reads it. */
interface ApiError {
  message?: string;
  response?: { data?: { error?: string } };
}

const ErrorDetailsModal = ({ errorResult, onClose }: ErrorDetailsModalProps) => {
  const [selectedRecord, setSelectedRecord] = useState<ErrorDetailRow | null>(null);
  const [showRecordModal, setShowRecordModal] = useState(false);
  const [exporting, setExporting] = useState<ExportFormat | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);
  const sample = errorResult.error_details || [];
  const total = totalErrorRows(errorResult);
  const needsFullRows = total > sample.length;
  const [details, setDetails] = useState<ErrorDetailsResponse | null>(null);
  const [loadingDetails, setLoadingDetails] = useState(needsFullRows);
  const [detailsError, setDetailsError] = useState<string | null>(null);
  const severity = errorResult.severity_level || 'none';
  const config = getSeverityConfig(severity);
  const thresholds = errorResult.severity_thresholds || {};

  // The sample is not every row: load the latest run's rows for the table.
  useEffect(() => {
    if (!needsFullRows) return;
    let cancelled = false;
    setLoadingDetails(true);
    api.errorDetection
      .getResultDetails(errorResult.rule_id)
      .then((response: { data: ErrorDetailsResponse }) => {
        if (!cancelled) setDetails(response.data);
      })
      .catch((err: ApiError) => {
        if (!cancelled) {
          setDetailsError(err.response?.data?.error || 'Could not load all the records');
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingDetails(false);
      });
    return () => {
      cancelled = true;
    };
  }, [errorResult.rule_id, needsFullRows]);

  const rows: ErrorDetailRow[] = details ? details.rows : sample;
  const shownAll = rows.length >= total;

  const handleRowClick = (record: ErrorDetailRow) => {
    setSelectedRecord(record);
    setShowRecordModal(true);
  };

  const handleExport = async (format: ExportFormat) => {
    setExporting(format);
    setExportError(null);
    try {
      const response = await api.errorDetection.export[format](errorResult.rule_id);
      const downloadUrl: string = response.data.downloadUrl;
      const fileResponse = await api.get(downloadUrl, { responseType: 'blob' });
      const blob = new Blob([fileResponse.data as BlobPart]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = response.data.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      const e = err as ApiError;
      const msg = e.response?.data?.error || e.message || 'Export failed';
      setExportError(msg);
    } finally {
      setExporting(null);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-surface rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col animate-slideUp"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div className="flex items-center gap-3 min-w-0">
            <div
              className={`flex items-center justify-center w-9 h-9 rounded-lg ${config.bg} ${config.border} border`}
            >
              <SeverityIcon severity={severity} />
            </div>
            <div className="min-w-0">
              <h2
                className="text-lg font-bold text-slate-900 truncate"
                style={{ fontFamily: 'DM Sans, sans-serif' }}
              >
                {errorResult.rule_name}
              </h2>
              <p className="text-xs text-slate-500 truncate">
                {errorResult.description || 'No description'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors shrink-0 ml-3"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {/* Summary KPIs */}
          <div className="grid grid-cols-3 gap-4">
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <div className="flex items-center gap-2 mb-1">
                <Hash className="w-3.5 h-3.5 text-slate-400" />
                <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
                  Error Count
                </span>
              </div>
              <p
                className={`text-2xl font-bold tabular-nums ${config.text}`}
                style={{ fontFamily: 'IBM Plex Mono, monospace' }}
              >
                {errorResult.error_count || 0}
              </p>
            </div>

            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <div className="flex items-center gap-2 mb-1">
                <SeverityIcon severity={severity} className="w-3.5 h-3.5" />
                <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
                  Severity
                </span>
              </div>
              <div className="mt-1">
                <SeverityBadge severity={severity} />
              </div>
            </div>

            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <div className="flex items-center gap-2 mb-1">
                <Clock className="w-3.5 h-3.5 text-slate-400" />
                <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
                  Last Checked
                </span>
              </div>
              <p className="text-sm font-medium text-slate-700 mt-1">
                {errorResult.checked_at
                  ? new Date(errorResult.checked_at).toLocaleString()
                  : 'Never'}
              </p>
            </div>
          </div>

          {/* Thresholds */}
          {(thresholds.warning || thresholds.critical || thresholds.armageddon) && (
            <div className="rounded-xl border border-primary-200 bg-primary-50/50 p-4">
              <p className="text-[10px] font-semibold text-primary-700 uppercase tracking-widest mb-2.5">
                Severity Thresholds
              </p>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-600">
                    <span className="w-2 h-2 rounded-full bg-amber-500" />
                    Warning
                  </span>
                  <p
                    className="text-sm font-bold text-slate-700 mt-0.5"
                    style={{ fontFamily: 'IBM Plex Mono, monospace' }}
                  >
                    {thresholds.warning}+ errors
                  </p>
                </div>
                <div>
                  <span className="inline-flex items-center gap-1 text-xs font-semibold text-orange-600">
                    <span className="w-2 h-2 rounded-full bg-orange-500" />
                    Critical
                  </span>
                  <p
                    className="text-sm font-bold text-slate-700 mt-0.5"
                    style={{ fontFamily: 'IBM Plex Mono, monospace' }}
                  >
                    {thresholds.critical}+ errors
                  </p>
                </div>
                <div>
                  <span className="inline-flex items-center gap-1 text-xs font-semibold text-red-600">
                    <span className="w-2 h-2 rounded-full bg-red-500" />
                    Armageddon
                  </span>
                  <p
                    className="text-sm font-bold text-slate-700 mt-0.5"
                    style={{ fontFamily: 'IBM Plex Mono, monospace' }}
                  >
                    {thresholds.armageddon}+ errors
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Error Details Table */}
          {rows.length > 0 || loadingDetails ? (
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-slate-800">
                  Error Details
                  <span className="ml-1.5 text-xs font-normal text-slate-400">
                    ({total.toLocaleString()} found)
                  </span>
                </h3>
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1 mr-2">
                    <button
                      onClick={() => handleExport('csv')}
                      disabled={!!exporting}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 transition-colors disabled:opacity-50"
                      title="Export to CSV"
                    >
                      {exporting === 'csv' ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <Download className="w-3 h-3" />
                      )}
                      CSV
                    </button>
                    <button
                      onClick={() => handleExport('excel')}
                      disabled={!!exporting}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200 transition-colors disabled:opacity-50"
                      title="Export to Excel"
                    >
                      {exporting === 'excel' ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <FileSpreadsheet className="w-3 h-3" />
                      )}
                      Excel
                    </button>
                    <button
                      onClick={() => handleExport('pdf')}
                      disabled={!!exporting || total > PDF_MAX_ROWS}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium text-red-700 bg-red-50 hover:bg-red-100 border border-red-200 transition-colors disabled:opacity-50"
                      title={total > PDF_MAX_ROWS ? 'PDF limited to 1000 rows' : 'Export to PDF'}
                    >
                      {exporting === 'pdf' ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <FileText className="w-3 h-3" />
                      )}
                      PDF
                    </button>
                  </div>
                  <span className="inline-flex items-center gap-1.5 text-[10px] text-primary-600 font-medium">
                    <MousePointerClick className="w-3 h-3" />
                    Click row to inspect
                  </span>
                </div>
              </div>
              {exportError && (
                <div className="mb-3 px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-xs text-red-700">
                  {exportError}
                </div>
              )}
              {detailsError && (
                <div className="mb-3 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800">
                  {detailsError}. Showing the first {rows.length.toLocaleString()} of{' '}
                  {total.toLocaleString()}.
                </div>
              )}
              {!loadingDetails && !detailsError && !shownAll && (
                <p className="mb-3 text-xs text-slate-500" data-testid="details-partial-note">
                  Showing the first {rows.length.toLocaleString()} of {total.toLocaleString()}{' '}
                  records. Export to CSV or Excel for all of them.
                </p>
              )}

              {loadingDetails ? (
                <div className="flex items-center justify-center gap-2 py-10 rounded-xl border border-slate-200 text-sm text-slate-500">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Loading {total.toLocaleString()} records…
                </div>
              ) : (
                <div className="rounded-xl border border-slate-200 overflow-hidden">
                  <div className="max-h-80 overflow-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-slate-50 border-b border-slate-200">
                          {Object.keys(rows[0] || {}).map((key) => (
                            <th
                              key={key}
                              className="px-4 py-2.5 text-left text-[10px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap sticky top-0 bg-slate-50"
                            >
                              {key}
                            </th>
                          ))}
                          <th className="w-8 sticky top-0 bg-slate-50" />
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {rows.map((record, index) => (
                          <tr
                            key={index}
                            onClick={() => handleRowClick(record)}
                            className="cursor-pointer hover:bg-primary-50/40 transition-colors group"
                          >
                            {Object.values(record).map((value, vIdx) => (
                              <td
                                key={vIdx}
                                className="px-4 py-2.5 text-slate-700 whitespace-nowrap"
                              >
                                {value !== null && value !== undefined ? (
                                  String(value)
                                ) : (
                                  <span className="text-slate-300 italic">null</span>
                                )}
                              </td>
                            ))}
                            <td className="px-2 py-2.5">
                              <ChevronRight className="w-3.5 h-3.5 text-slate-300 group-hover:text-primary-500 transition-colors" />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-emerald-100 mb-3">
                <CheckCircle className="w-7 h-7 text-emerald-500" />
              </div>
              <h3 className="text-base font-semibold text-slate-800 mb-1">No Errors Found</h3>
              <p className="text-sm text-slate-500">
                This rule is not detecting any issues currently.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end px-6 py-4 border-t border-slate-100">
          <button
            onClick={onClose}
            className="px-4 py-2.5 rounded-lg text-sm font-semibold text-slate-600 hover:bg-slate-100 transition-colors"
          >
            Close
          </button>
        </div>
      </div>

      {/* Nested Record Modal */}
      {showRecordModal && selectedRecord && (
        <ErrorRecordModal
          errorRecord={selectedRecord}
          errorResult={errorResult}
          onClose={() => setShowRecordModal(false)}
        />
      )}
    </div>
  );
};

export default ErrorDetailsModal;
